import { of } from 'rxjs';
import { cacheCatchError, doomed } from '@banking/ui/utils/rxjs';

/**
 * Because Workflows expose an Observable of "state" and Observables cannot
 * continue after producing error (and are thus considered complete) there
 * sometimes is a need to "do cleanup" and produce a "final state" for
 * subscribers (renderings) to utilize.
 *
 * The "recovery experiment" allows for catching unhandled errors within a
 * workflow and calculating the final state with a similar API as update: by
 * "reducing" previous state and action, but instead with previous state and
 * the thrown value.
 *
 * The recover function is expected to return a tuple of State and an optional
 * second value that will be rethrown for the workflow's Observable<State> to
 * handle (unless it is null or undefined — we don't throw those cuz y).
 *
 * ## Example
 *
 * When catching and handling the error completely.
 *
 * ```ts
 * function recover(state: State, error: any): Recovery<State> {
 *   const errorMessage = error.message ?? 'Something went wrong.';
 *   return [{...state, errorMessage}, null];
 * }
 * ```
 *
 * When catching and rethrowing.
 *
 * ```ts
 * function recover(state: State, error: any): Recovery<State> {
 *   const errorMessage = error.message ?? 'Something went wrong.';
 *   return [{...state, errorMessage}, error];
 * }
 * ```
 *
 * ### Usage
 *
 * The recovery experiment is currently exposed as an Observable operator to be
 * used on the workflow library's exposed Observable<State> rather than cooked
 * into the library itself. The latter is the goal if this experiment
 * "succeeds."
 *
 * ```ts
 * function update(state: number|null, action: "increment"|"decrement"): Update<number, "increment"|"decrement"> {
 *   const newState = action === "increment" ? state + 1 : state - 1;
 *   if (newState < 0) {
 *     throw new Error('I don't deal with negative numbers');
 *   }
 *   return [newState, null];
 * }
 *
 * function recover(state: number|null, error: any): Recovery<number> {
 *   const rethrow = error.message === 'I don't deal with negative numbers' ? null : error;
 *   return [null, rethrow];
 * }
 *
 * const myWorkflow = workflow(update, 0);
 * const states = myWorkflow.states.pipe(recovery(recover, context));
 * states.subscribe(console.log, () => console.log('an error happened'));
 * myWorkflow.emit('increment');
 * // 1
 * myWorkflow.emit('decrement');
 * // 0
 * myWorkflow.emit('decrement');
 * // null, an error happened
 * ```
 */

export function recovery(recover, context) {
  return function (source) {
    return source.pipe(cacheCatchError((error, value) => {
      const [state, err] = recover(value, error, context);
      return err == null ? of(state) : doomed(state)(err);
    }));
  };
}